/**
 * User Form Modal
 *
 * Modal for creating/editing users
 * Includes modality subscription management for radiologists
 */

import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/presentation/components/ui/Dialog'
import { Input } from '@/presentation/components/ui/Input'
import { Select } from '@/presentation/components/ui/Select'
import { Button } from '@/presentation/components/ui/Button'
import { ModalitySubscriptionManager } from '@/presentation/components/users/ModalitySubscriptionManager'
import { RadiologosDelTranscriptor } from '@/presentation/components/users/RadiologosDelTranscriptor'
import { useCreateUser, useUpdateUser } from '@/application/users/useUsers'
import { useInstitutions } from '@/application/institutions'
import { useAuth } from '@/application/auth'
import { USER_ROLES } from '@/config/constants'
import { toast } from '@/lib/toast'
import type { UserWithInstitution } from '@/domain/user/types'

// =============================================================================
// Schema
// =============================================================================

const createUserSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(8, 'La contraseña debe tener al menos 8 caracteres'),
  role: z.enum(['ADMIN', 'RADIOLOGIST', 'DOCTOR', 'TECHNICIAN', 'TRANSCRIPTOR']),
  firstName: z.string().min(1, 'El nombre es requerido'),
  lastName: z.string().min(1, 'El apellido es requerido'),
  secondLastName: z.string().optional(),
  phone: z.string().optional(),
  institutionId: z.string().optional(),
})

const updateUserSchema = z.object({
  email: z.string().email('Email inválido').optional(),
  role: z.enum(['ADMIN', 'RADIOLOGIST', 'DOCTOR', 'TECHNICIAN', 'TRANSCRIPTOR']).optional(),
  firstName: z.string().min(1, 'El nombre es requerido').optional(),
  lastName: z.string().min(1, 'El apellido es requerido').optional(),
  secondLastName: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),
})

type CreateFormData = z.infer<typeof createUserSchema>
type UpdateFormData = z.infer<typeof updateUserSchema>

/**
 * Roles que puede crear el administrador de una IPS.
 *
 * `modulo` marca los que solo existen si la institución lo contrató. Ofrecer
 * un rol cuyo módulo está apagado crea una cuenta que no puede entrar, y la
 * llamada de soporte llega después, cuando esa persona lo intenta.
 */
const ROLE_OPTIONS = [
  { value: 'ADMIN', label: 'Administrador' },
  { value: 'RADIOLOGIST', label: 'Radiólogo' },
  { value: 'DOCTOR', label: 'Médico remitente', modulo: 'medico_remitente' },
  { value: 'TECHNICIAN', label: 'Técnico' },
  { value: 'TRANSCRIPTOR', label: 'Transcriptor', modulo: 'transcripcion' },
]

// =============================================================================
// Component
// =============================================================================

interface UserFormModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user?: UserWithInstitution | null
}

export function UserFormModal({ open, onOpenChange, user }: UserFormModalProps) {
  const { user: currentUser } = useAuth()
  const isSuperAdmin = currentUser?.role === USER_ROLES.SUPER_ADMIN

  // Los roles cuyo módulo no está contratado no se ofrecen. Se conserva el
  // del usuario que se está editando, para no cambiárselo sin querer.
  const modulos = currentUser?.modulos
  const rolesDisponibles = ROLE_OPTIONS.filter(
    (o) => !o.modulo || modulos?.[o.modulo] === true || o.value === user?.role
  )
  const isEditing = !!user
  const [apiError, setApiError] = useState<string | null>(null)
  const createMutation = useCreateUser()
  const updateMutation = useUpdateUser()

  // Load institutions for SUPER_ADMIN
  const { data: institutionsData } = useInstitutions({ limit: 100, isActive: true })
  const institutionOptions = [
    { value: '', label: 'Seleccionar institución...' },
    ...(institutionsData?.institutions.map((inst) => ({
      value: inst.id,
      label: inst.name,
    })) || []),
  ]

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<CreateFormData | UpdateFormData>({
    resolver: zodResolver(isEditing ? updateUserSchema : createUserSchema),
    defaultValues: {
      email: '',
      password: '',
      role: 'RADIOLOGIST',
      firstName: '',
      lastName: '',
      secondLastName: '',
      phone: '',
      institutionId: '',
    },
  })

  // Watch the role field to conditionally show modality subscriptions
  const selectedRole = useWatch({ control, name: 'role' })

  // Reset form when modal opens/closes or user changes
  useEffect(() => {
    if (open) {
      setApiError(null)
      if (user) {
        reset({
          email: user.email,
          role: user.role as CreateFormData['role'],
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          secondLastName: user.secondLastName || '',
          phone: user.phone || '',
        })
      } else {
        reset({
          email: '',
          password: '',
          role: 'RADIOLOGIST',
          firstName: '',
          lastName: '',
          secondLastName: '',
          phone: '',
          institutionId: '',
        })
      }
    }
  }, [open, user, reset])

  const onSubmit = async (data: CreateFormData | UpdateFormData) => {
    setApiError(null)

    if (isEditing) {
      updateMutation.mutate(
        { id: user.id, data: data as UpdateFormData },
        {
          onSuccess: () => {
            toast.success('Usuario actualizado')
            onOpenChange(false)
          },
          onError: (error: any) => {
            const message = error.response?.data?.message || error.message
            setApiError(message)
            toast.error(message || 'Error al actualizar usuario')
          },
        }
      )
    } else {
      // Clean up institutionId if empty
      const createData = { ...data } as CreateFormData
      if (!createData.institutionId) {
        delete createData.institutionId
      }
      createMutation.mutate(createData, {
        onSuccess: () => {
          toast.success('Usuario creado')
          onOpenChange(false)
        },
        onError: (error: any) => {
          const message = error.response?.data?.message || error.message
          setApiError(message)
          toast.error(message || 'Error al crear usuario')
        },
      })
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  // Check if we should show the modality subscription manager
  const showModalitySubscriptions = isEditing && user && selectedRole === 'RADIOLOGIST'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={showModalitySubscriptions ? 'w-full max-w-[95vw] sm:max-w-2xl' : 'w-full max-w-[95vw] sm:max-w-md'}>
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Modifica los datos del usuario'
              : 'Ingresa los datos para crear un nuevo usuario'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto px-1">
          {/* API Error */}
          {apiError && (
            <div className="p-3 rounded-lg bg-[var(--destructive)]/10 border border-[var(--destructive)]/20 text-[var(--destructive)] text-sm">
              {apiError}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Nombre *"
              placeholder="Juan"
              error={(errors as any).firstName?.message}
              {...register('firstName')}
            />
            <Input
              label="Primer Apellido *"
              placeholder="García"
              error={(errors as any).lastName?.message}
              {...register('lastName')}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Segundo Apellido"
              placeholder="Rodríguez"
              error={(errors as any).secondLastName?.message}
              {...register('secondLastName')}
            />
            <Input
              label="Teléfono"
              placeholder="+57 300 123 4567"
              error={(errors as any).phone?.message}
              {...register('phone')}
            />
          </div>

          <Input
            label="Email *"
            type="email"
            placeholder="usuario@hospital.com"
            error={errors.email?.message}
            {...register('email')}
          />

          {!isEditing && (
            <Input
              label="Contraseña *"
              type="password"
              placeholder="********"
              error={(errors as { password?: { message?: string } }).password?.message}
              {...register('password')}
            />
          )}

          <Select
            label="Rol *"
            options={rolesDisponibles}
            error={errors.role?.message}
            {...register('role')}
          />

          {/* Institution selector - only for SUPER_ADMIN when creating */}
          {isSuperAdmin && !isEditing && (
            <Select
              label="Institución *"
              options={institutionOptions}
              error={(errors as { institutionId?: { message?: string } }).institutionId?.message}
              {...register('institutionId', {
                validate: (value) => (value && value !== '') || 'Debe seleccionar una institución'
              })}
            />
          )}

          {/* A quien transcribe: solo para transcriptores, al editar.
              Igual que las modalidades de un radiologo, se administra sobre
              un usuario que ya existe. */}
          {isEditing && user && (selectedRole || user.role) === 'TRANSCRIPTOR' && (
            <div className="mt-6 pt-6 border-t border-[var(--border)]">
              <RadiologosDelTranscriptor userId={user.id} />
            </div>
          )}

          {/* Modality Subscriptions - only for radiologists when editing */}
          {showModalitySubscriptions && (
            <div className="mt-6 pt-6 border-t border-[var(--border)]">
              <ModalitySubscriptionManager
                userId={user.id}
                userRole={selectedRole || user.role}
              />
            </div>
          )}

          <DialogFooter className="mt-6 flex-col-reverse sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
              className="w-full sm:w-auto"
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending} className="w-full sm:w-auto">
              {isPending
                ? 'Guardando...'
                : isEditing
                  ? 'Guardar cambios'
                  : 'Crear usuario'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
